import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { FormField } from 'components/create-consignment/create-consignment.types';
import { dangerousGoodFields } from 'components/create-consignment/create-modal.constants';
import { useTranslation } from 'react-i18next';
import { formRules } from 'library/constants';
import { StylesProps } from 'theme/jss-types';
import {
    getDangerousGoodData,
} from '../../../../network/consignments.api';

import {
    Col,
    Form,
    Input,
    message,
    FormInstance,
} from 'antd';

const styles = () => ({
    wrapper: {
        gap: '10px',
    },
    colWrapper: {
    },
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    form: FormInstance;
    params: any;
}
const DangerousGoodFields = (props: IProps) => {
    const { t, i18n } = useTranslation();
    const {
        classes,
        form,
        params,
    } = props;

    const {
        pieceIndex,
        getValidationRules,
        getIsRequiredValidation,
        disabledFields = {},
        productCode,
    } = params;
    const isPieceLevel = pieceIndex !== null && pieceIndex !== undefined;
    const {
        DangerousGoodUnNumber,
        DangerousGoodName,
        DangerousGoodClass,
        DangerousGoodDivision,
    } = dangerousGoodFields;

    const dangerousDivisionValue = form.getFieldValue([isPieceLevel ? `piece_${DangerousGoodDivision.key}_${pieceIndex}`
        : DangerousGoodDivision.key]);
    const dangerousClassValue = form.getFieldValue([isPieceLevel ? `piece_${DangerousGoodClass.key}_${pieceIndex}`
        : DangerousGoodClass.key]);
    const dangerousNameValue = form.getFieldValue([isPieceLevel ? `piece_${DangerousGoodName.key}_${pieceIndex}`
        : DangerousGoodName.key]);
    const [dangerousDivision, setDangerousDivision] = React.useState<any>(dangerousDivisionValue);
    const [dangerousClass, setDangerousClass] = React.useState<any>(dangerousClassValue);
    const [dangerousName, setDangerousName] = React.useState<any>(dangerousNameValue);
    const dangerousGoodUnNumberKey = isPieceLevel
        ? `piece_${DangerousGoodUnNumber.key}_${pieceIndex}` : DangerousGoodUnNumber.key;
    const formRuleRequired = {
        ...formRules.required,
        message: i18n.exists('required') ? t('required') : 'Required',
    };

    const validateRegex = (value: string) => {
        const regex = /^[Uu][Nn](?:[0-2]\d{3}|3[0-5]\d{2}|3600)$/;
        return regex.test(value);
    };

    const fetchUnNumberData = async (value: string) => {
        if (value && validateRegex(value)) {
            const unNumber = value?.toString()?.toUpperCase();
            const response = await getDangerousGoodData({ unNumber });
            if (response.isSuccess) {
                setDangerousClass(response?.data?.class);
                setDangerousDivision(response?.data?.division);
                setDangerousName(response?.data?.shipping_name);
                form.setFieldsValue({
                    [isPieceLevel ? `piece_${DangerousGoodClass.key}_${pieceIndex}`
                        : DangerousGoodClass.key]: response?.data?.class,
                    [isPieceLevel ? `piece_${DangerousGoodDivision.key}_${pieceIndex}`
                        : DangerousGoodDivision.key]: response?.data?.division,
                    [isPieceLevel ? `piece_${DangerousGoodName.key}_${pieceIndex}`
                        : DangerousGoodName.key]: response?.data?.shipping_name,
                });
            } else {
                message.error(response?.errorMessage || 'Invalid input!');
            }
        } else {
            form.setFieldsValue({
                [isPieceLevel ? `piece_${DangerousGoodClass.key}_${pieceIndex}`
                    : DangerousGoodClass.key]: undefined,
                [isPieceLevel ? `piece_${DangerousGoodDivision.key}_${pieceIndex}`
                    : DangerousGoodDivision.key]: undefined,
                [isPieceLevel ? `piece_${DangerousGoodName.key}_${pieceIndex}`
                    : DangerousGoodName.key]: undefined,
            });
            setDangerousClass(undefined);
            setDangerousDivision(undefined);
            setDangerousName(undefined);
        }
    };

    React.useEffect(() => {
        fetchUnNumberData(form.getFieldValue([dangerousGoodUnNumberKey]));
    }, [productCode]);

    const renderField = (field: FormField, value: string | number | undefined) => {
        const dangerousGoodKey = isPieceLevel ? `piece_${field.key}_${pieceIndex}` : field.key;
        return (
            <Col span={24} xs={24} sm={24} md={12} className={classes.colWrapper}>
                <div>
                    <div
                        className={classes.title}
                        style={{ marginBottom: '5px' }}
                    >
                        {t(field.label)}
                        {getIsRequiredValidation(field.key, isPieceLevel) && '*'}
                    </div>
                    <Form.Item
                        name={dangerousGoodKey}
                        rules={field.validate
                            ? [formRuleRequired, ...getValidationRules(field, isPieceLevel)]
                            : getValidationRules(field, isPieceLevel)}
                    >
                        <Input
                            disabled
                            value={value}
                        />
                    </Form.Item>
                </div>
            </Col>
        );
    };

    return (
        <>
            <Col span={24} xs={24} sm={24} md={12} className={classes.colWrapper}>
                <div>
                    <div
                        className={classes.title}
                        style={{ marginBottom: '5px' }}
                    >
                        {t(DangerousGoodUnNumber.label)}
                        {getIsRequiredValidation(DangerousGoodUnNumber.key, isPieceLevel) && '*'}
                    </div>
                    <Form.Item
                        name={dangerousGoodUnNumberKey}
                        initialValue={DangerousGoodUnNumber.defaultValue}
                        valuePropName={DangerousGoodUnNumber.valuePropName}
                        rules={[
                            {
                                validator: (rule, value, cb) => {
                                    if (value && !validateRegex(value)) {
                                        return cb('UN Number can be from UN000 to UN3600');
                                    }
                                    return cb();
                                },
                            },
                            ...getValidationRules(DangerousGoodUnNumber, isPieceLevel),
                        ]}
                    >
                        <Input
                            placeholder={t(DangerousGoodUnNumber.key)}
                            onInput={(e) => {
                                (e.target as HTMLInputElement).value = (e.target as HTMLInputElement)
                                    .value?.toUpperCase();
                            }}
                            onChange={(e) => fetchUnNumberData(e.target.value)}
                            disabled={disabledFields[dangerousGoodUnNumberKey]
                                || disabledFields.dangerousGood
                                || (isPieceLevel && disabledFields.piece)
                                || (isPieceLevel && disabledFields[`piece_${DangerousGoodUnNumber.key}`])}
                        />
                    </Form.Item>
                </div>
            </Col>
            {dangerousName && renderField(DangerousGoodName, dangerousName)}
            {dangerousClass && renderField(DangerousGoodClass, dangerousClass)}
            {dangerousDivision && renderField(DangerousGoodDivision, dangerousDivision)}
        </>
    );
};

const mapStateToProps = () => {
    return {};
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(DangerousGoodFields);
